import React, { useEffect, useState } from 'react';
import type { Country } from 'baseui/phone-input';
import { styled } from 'baseui';
import { useQuery } from '@tanstack/react-query';
import { ChevronDown } from 'lucide-react';
import { usePhoneFormat } from '../hooks/usePhoneFormat';
import { FormControl } from 'baseui/form-control';
import { getCountries, type Country as ApiCountry } from '../api/publicApi';
import { useTenantBranding } from '../hooks/useTenantBranding';

interface FormattedPhoneInputProps {
  value: string;
  country: Country;
  onChange: (value: string, country: Country) => void;
  error?: boolean | string;
  disabled?: boolean;
  required?: boolean;
  placeholder?: string;
  label?: string;
  caption?: string;
  showFormatHint?: boolean;
}

const FormatHint = styled('div', {
  marginTop: '4px',
  fontSize: '12px',
  color: '#666',
});

// Function to format Dominican Republic phone numbers
const formatDominicanPhoneNumber = (phoneNumber: string): string => {
  // Remove all non-digit characters
  let digits = phoneNumber.replace(/\D/g, '');
  
  // Remove the country code (+1) if present at the start
  if (digits.startsWith('1')) {
    digits = digits.slice(1);
  }
  
  // Format the remaining digits incrementally
  let formatted = '';
  
  if (digits.length > 0) {
    // Add opening parenthesis for area code
    formatted = '(';
    formatted += digits.slice(0, 3);
    
    if (digits.length >= 3) {
      // Close parenthesis after area code
      formatted += ') ';
      if (digits.length > 3) {
        // Add next three digits
        formatted += digits.slice(3, 6);
        
        if (digits.length > 6) {
          // Add hyphen and remaining digits
          formatted += '-' + digits.slice(6);
        }
      }
    }
  }
  
  return formatted;
};

export const FormattedPhoneInput: React.FC<FormattedPhoneInputProps> = ({
  value,
  country,
  onChange,
  error = false,
  disabled = false,
  required = false,
  placeholder = 'Enter phone number',
  label,
  caption,
  showFormatHint = true,
}) => {
  const [inputValue, setInputValue] = useState(value);
  const [selectedCountry, setSelectedCountry] = useState<Country>(country);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [search, setSearch] = useState('');
  // Same /country list the public events flow uses: flag + dial code per country.
  const { tenantApiKey, tenantLoading } = useTenantBranding();
  const { data: countries = [] } = useQuery({
    queryKey: ['countries', tenantApiKey],
    queryFn: () => getCountries(tenantApiKey || undefined),
    enabled: !tenantLoading,
    staleTime: Infinity,
  });
  const selectedApi = countries.find((c) => c.country_code === selectedCountry.id);
  const filtered = countries.filter(
    (c) => !search || (c.country || '').toLowerCase().includes(search.toLowerCase()) || (c.phone_code || '').includes(search),
  );
  
  const { 
    formattedPhoneNumber, 
    fullPhoneNumber,
    isValid: isValidFormat, 
    error: formatError,
    phonePattern,
    countryData
  } = usePhoneFormat({
    countryCode: selectedCountry.id,
    phoneNumber: inputValue
  });

  // Handle country change
  const handleCountryChange = (c: ApiCountry) => {
    const option = { id: c.country_code, label: c.country, dialCode: c.phone_code } as Country;
    setSelectedCountry(option);
    // When changing country, clear input or reformat as needed
    setInputValue('');
    onChange('', option);
    setPickerOpen(false);
    setSearch('');
  };

  // Handle text change
  const handleTextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    let localNumber = newValue.replace(/\D/g, '');
    let formattedValue = localNumber;
    if (selectedCountry.id === 'DO') {
      // Remove leading '1' if present
      if (localNumber.startsWith('1')) {
        localNumber = localNumber.slice(1);
      }
      formattedValue = formatDominicanPhoneNumber(localNumber);
      setInputValue(formattedValue);
      // Always send with +countrycode
      onChange(`+${selectedCountry.dialCode.replace('+', '')}${localNumber}`, selectedCountry);
    } else {
      setInputValue(localNumber);
      onChange(`+${selectedCountry.dialCode.replace('+', '')}${localNumber}`, selectedCountry);
    }
  };

  // Handle blur to show formatted value (for DO)
  const handleBlur = () => {
    if (inputValue.trim() !== '' && selectedCountry.id === 'DO') {
      const digits = inputValue.replace(/\D/g, '');
      const formattedValue = formatDominicanPhoneNumber(digits);
      setInputValue(formattedValue);
    }
  };

  // Handle focus
  const handleFocus = () => {
    // No necesitamos cambiar el formato al enfocar
  };

  // Determine error message
  const errorMessage = typeof error === 'string' 
    ? error 
    : (error && !isValidFormat && inputValue.trim() !== '' 
      ? selectedCountry.id === 'DO'
        ? 'Invalid phone number format (expected 10 digits)'
        : `Invalid phone number format${
            phonePattern ? ` (expected ${phonePattern.len} digits)` : ''
          }`
      : undefined);

  // Create format hint message
  const formatHintMessage = selectedCountry.id === 'DO'
    ? ''//'Format: (XXX) XXX-XXXX'
    : (countryData && phonePattern && showFormatHint
      ? `Format: ${countryData.phone_code} ${phonePattern.pattern}`
      : '');

  // Validate Dominican Republic phone number
  const isDominicanNumberValid = (number: string): boolean => {
    if (selectedCountry.id === 'DO') {
      const digits = number.replace(/\D/g, '');
      // Remove leading '1' if present
      const cleanDigits = digits.startsWith('1') ? digits.slice(1) : digits;
      // We expect 10 digits for Dominican Republic numbers (not counting the country code)
      return cleanDigits.length >= 10;
    }
    return isValidFormat;
  };

  const isValid = isDominicanNumberValid(inputValue);

  return (
    <FormControl
      label={label}
      caption={caption || (formatError || '')}
      error={errorMessage}
    >
      <div style={{ position: 'relative' }}>
        {/* Hand-rolled (Tailwind + tokens), like ui/PhoneInput: BaseUI's PhoneInput lost its
            country select styles in deployed builds (flag clipped, empty box, dial code apart). */}
        <div
          className="flex items-stretch h-10 w-full overflow-hidden rounded-[8px] border border-solid bg-white"
          style={{ borderColor: errorMessage ? 'var(--danger, #c0392b)' : 'var(--line, #E2E2E2)' }}
        >
          <button
            type="button"
            disabled={disabled}
            aria-label={`Country: ${selectedCountry.label} ${selectedCountry.dialCode}`}
            aria-haspopup="dialog"
            onClick={() => setPickerOpen(true)}
            className="flex flex-none items-center gap-1.5 border-0 border-r border-solid bg-transparent px-2.5 text-sm text-[var(--ink-900)] cursor-pointer disabled:cursor-default"
            style={{ borderRightColor: 'var(--line, #E2E2E2)' }}
          >
            {selectedApi?.country_flag
              ? <img src={selectedApi.country_flag} alt="" style={{ width: 22, height: 16, borderRadius: 2, objectFit: 'cover' }} />
              : <span aria-hidden style={{ width: 22, height: 16, borderRadius: 2, background: 'var(--line, #E2E2E2)' }} />}
            <span>{selectedCountry.dialCode}</span>
            <ChevronDown size={14} aria-hidden />
          </button>
          <input
            id="phone-input"
            name="phone"
            type="tel"
            aria-label="Phone number input"
            className="min-w-0 flex-1 border-0 bg-transparent px-3 text-sm text-[var(--ink-900)] outline-none placeholder:text-[var(--ink-400)]"
            // Inline: inside a `.fc` form control, `.fc input` (global.css) would otherwise give this input its
            // own border, grey fill and padding, which reads as an empty input laid over the country trigger.
            style={{ boxShadow: 'none', border: 'none', borderRadius: 0, background: 'transparent', padding: '0 12px', width: 'auto' }}
            placeholder={placeholder}
            disabled={disabled}
            required={required}
            value={inputValue}
            onChange={handleTextChange}
            onBlur={handleBlur}
          />
        </div>
        {pickerOpen && (
          <div
            role="dialog"
            aria-label="Select country"
            onClick={() => { setPickerOpen(false); setSearch(''); }}
            style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.3)', zIndex: 20000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}
          >
            <div
              onClick={(e) => e.stopPropagation()}
              style={{ backgroundColor: '#fff', borderRadius: 12, width: '100%', maxWidth: 400, maxHeight: '70vh', padding: 16, display: 'flex', flexDirection: 'column' }}
            >
              <input
                autoFocus
                aria-label="Search country"
                className="mb-2 h-10 w-full rounded-[8px] border border-solid bg-white px-3 text-sm outline-none"
                style={{ borderColor: 'var(--line, #E2E2E2)' }}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search country…"
              />
              <div style={{ flex: 1, overflowY: 'auto' }}>
                {filtered.map((c) => (
                  <div
                    key={c.country_code}
                    role="option"
                    aria-selected={c.country_code === selectedCountry.id}
                    onClick={() => handleCountryChange(c)}
                    style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 8px', cursor: 'pointer', borderRadius: 6, fontSize: 14 }}
                  >
                    <img src={c.country_flag} alt="" style={{ width: 24, height: 18, borderRadius: 2 }} />
                    <span style={{ flex: 1 }}>{c.country}</span>
                    <span style={{ color: '#6B7280' }}>{c.phone_code}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
        {formatHintMessage && !errorMessage && (
          <FormatHint>
            {formatHintMessage}
          </FormatHint>
        )}
      </div>
    </FormControl>
  );
}; 